import React, * as react from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as kratos from '@ory/kratos-client';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';

import { useReturn } from './ReturnTo';

import {
  kratosApi,
  BodyValues,
  bodyValueExtractor
} from './kratos/common';

import { useInitFlow } from './kratos/useInitFlow';
import Flow from './kratos/Flow';


/*
React Component providing a Login UI.
*/
export default function Login(props: LoginProps) {
  const flowType = 'login';
  const isRefresh = props.isRefreshLogin === true;
  const returnHelper = useReturn();
  const queryClient = useQueryClient();

  const {
    flowId, initFlowQuery, initFlowStatusElement
  } = useInitFlow(flowType, initFlow(isRefresh));

  if (flowId === '') {
    return initFlowStatusElement;
  }

  const flow = (
    <Flow
      flowId={flowId}
      flowType={flowType}
      flowGetter={getFlow}
      flowSubmitter={submitFlow(flowId)}
      afterSuccessfulSubmit={returnHelper.go} />);

  return (
    <Container sx={{ paddingTop: '3em', paddingBottom: '3em' }}>
      <Stack spacing={4}>
        <Typography align="center" variant="h3">
          Capybara Identity Portal
        </Typography>

        <Typography align="center" variant="h5">
          Please log into your Capybara account.
        </Typography>

        {flow}

        <Button component={Link} to="/recovery" variant="outlined">
          Reset Password
        </Button>
      </Stack>
    </Container>);
}


interface LoginProps
{
  isRefreshLogin?: boolean;
}


const initFlow = (isRefresh: boolean) => () => async () => await
  kratosApi.createBrowserLoginFlow({ refresh: isRefresh });

const getFlow = (id: string) => async () => await
  kratosApi.getLoginFlow({ id });

function submitFlow(flowId: string) {
  const flowID = flowId;

  async function onSubmit(
    values: BodyValues,
    submitGroup: string,
    submitter: string
  ) {
    const v = bodyValueExtractor(values);

    let body: kratos.UpdateLoginFlowBody | null = null;

    if (submitGroup === 'password') {
      body = {
        csrf_token: v('default-csrf_token'),
        identifier: v('default-identifier'),
        method: 'password',
        password: v('password-password'),
      };
    }

    if (submitGroup === 'oidc') {
      body = {
        csrf_token: v('default-csrf_token'),
        method: 'oidc',
        provider: submitter
      };
    }

    if (body === null) {
      throw new Error(`unavailable method ${submitGroup}`);
    }

    const response = kratosApi.updateLoginFlow({
      flow: flowId, updateLoginFlowBody: body
    });
    return response;
  }
  return onSubmit;
}
